'use client';

import React, { FC, useMemo } from 'react';

import Image from 'next/image';

import { useFlags } from 'launchdarkly-react-client-sdk';

import classNames from 'classnames';

import { Button, GenExternalLink, GenInternalLink } from '@/components';
import { BUTTON_SIZES } from '@/components/ui/Buttons/utils';

import { TypeUserRecord } from '@/lib/api/accountManager/types';
import { isSubscriptionAddonActive } from '@/lib/api/subscriptionManager/helpers/subscriptionAddonHelpers';

import {
  isItemOnlySkippableWithCXIntervention,
  isItemSkippable,
} from '@/lib/api/subscriptionManager/helpers/scheduledItemHelpers';

import {
  isSubscriptionActive,
  isSubscriptionCancellationAllowed,
  isSubscriptionOnlyCancellableWithCXIntervention,
  isSubscriptionReactivationAllowed,
} from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';

import {
  ADDON_KIND,
  TypeProductDecoratedScheduledItem,
  TypeScheduledItemRecord,
  TypeSubscriptionRecord,
} from '@/lib/api/subscriptionManager/types';

import bookAddonIcon from '@/public/assets/icons/book-addon-brush-stroke.svg';

import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackCancelSubscriptionEvent,
  trackItemClickedEvent,
  trackSkipKitFlowStartedEvent,
} from '@/utils/rudderstack';

import formatPossessiveProperNoun from '@/utils/translations/formatPossessiveProperNoun';
import interpolate from '@/utils/translations/interpolate';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';

import {
  _findChildForSubscription,
  _getActionLinks,
  _isPrepaidBundle,
  canPrepayForSubscription,
  generateSubscriptionEventPayload,
} from '../utils';

type SubscriptionActionsProps = {
  t: TypeAccountSettingsPageTranslations;
  locale: string | undefined;
  user: TypeUserRecord;
  subscription: TypeSubscriptionRecord;
  nextUnprocessedItem?: TypeScheduledItemRecord;
  isLanguageSettingsActionEnabled: boolean;
  productDecoratedItem: TypeProductDecoratedScheduledItem;
  isCancelSaverProgramEnabled: boolean;
};

type BookBundleAddonActionsProps = {
  t: TypeAccountSettingsPageTranslations;
  subscription: TypeSubscriptionRecord;
  removeBookBundleAddonLink: string;
};

const sendItemClickedEvent = (url: string, cta: CTA_NAMES) => {
  trackItemClickedEvent({
    url: url,
    type: CLICKED_ITEM_TYPES.LINK,
    pageLocation: PAGE_LOCATIONS.MANAGE_SUBSCRIPTION_TILE,
    cta: cta,
  });
};

const SubscriptionBookBundleAddOnActions: FC<BookBundleAddonActionsProps> = ({
  t,
  subscription,
  removeBookBundleAddonLink,
}) => {
  if (!isSubscriptionAddonActive(subscription, ADDON_KIND.BOOKS)) {
    return null;
  }

  return (
    <div className="ml-8 mt-4 flex items-center gap-4">
      <Image
        src={bookAddonIcon}
        alt="open book icon with lime green watercolor"
        height={100}
        width={100}
      />

      <div>
        <p className="mb-2 font-bold">{t.bookBundleAddonLabel}</p>

        <GenExternalLink
          href={removeBookBundleAddonLink}
          cta={t.removeBooksCta}
          onClick={() =>
            sendItemClickedEvent(
              removeBookBundleAddonLink,
              CTA_NAMES.REMOVE_BOOK_BUNDLE,
            )
          }
        />
      </div>
    </div>
  );
};

export interface EditPaymentScheduleLinkProps {
  locale: string | undefined;
  subscription: TypeSubscriptionRecord;
  t: TypeAccountSettingsPageTranslations;
  user: TypeUserRecord;
  nextUnprocessedItem?: TypeScheduledItemRecord;
  prepayAndSaveLink: string;
  continueGiftLink: string;
  isCancelSaverProgramEnabled: boolean;
}

const EditPaymentScheduleLink: FC<EditPaymentScheduleLinkProps> = ({
  subscription,
  t,
  user,
  locale,
  nextUnprocessedItem,
  prepayAndSaveLink,
  continueGiftLink,
  isCancelSaverProgramEnabled,
}) => {
  const status = useMemo(() => {
    const hasBundleId = subscription.bundle_discount_uuid;

    if (
      subscription.subscription_status !== 'active' &&
      subscription.subscription_source === 'GIFTING_ACTIVATION'
    ) {
      return 'inactive_gifted';
    }

    if (subscription.subscription_status !== 'active') {
      return 'inactive';
    }

    if (subscription.subscription_source === 'GIFTING_ACTIVATION') {
      return 'gifted';
    }

    if (_isPrepaidBundle(nextUnprocessedItem) && !hasBundleId) {
      return 'prepaid';
    }

    if (hasBundleId) {
      return 'repuf';
    }

    return 'pay-per-kit';
  }, [nextUnprocessedItem, subscription]);

  const currentSchedulePrefix = useMemo(() => {
    const child = _findChildForSubscription(subscription, user);

    const name = child && formatPossessiveProperNoun(child.name, locale);

    if (!name) return '';

    return name
      ? interpolate(t.playKitPaymentSchedule, {
          childName: name,
        })
      : '';
  }, [locale, subscription, t.playKitPaymentSchedule, user]);

  const currentScheduleLabel = useMemo(() => {
    if (status === 'gifted') {
      return t.scheduleGifted;
    } else if (status === 'repuf') {
      return t.scheduleRepuf;
    } else if (status === 'prepaid') {
      return t.schedulePrepaySave;
    } else if (status === 'pay-per-kit') {
      return t.schedulePayPerKit;
    }
  }, [t, status]);

  return (
    <div>
      {status !== 'inactive' && status !== 'inactive_gifted' && (
        <div className="mt-4">
          {currentSchedulePrefix}: {currentScheduleLabel}
        </div>
      )}

      <div>
        {(status === 'gifted' || status === 'inactive_gifted') && (
          <GenInternalLink
            href={continueGiftLink}
            cta={t.subscribeNowCta}
            testId="continue-gift-link"
            onClick={() =>
              sendItemClickedEvent(continueGiftLink, CTA_NAMES.SUBSCRIBE_NOW)
            }
            classes={classNames({
              'text-base': isCancelSaverProgramEnabled,
            })}
          />
        )}

        {status === 'repuf' && (
          <GenInternalLink
            href={`/account/payment-schedule/${subscription.uuid}`}
            cta={t.editPaymentScheduleCta}
            testId="edit-schedule-link"
            classes={classNames({
              'text-base': isCancelSaverProgramEnabled,
            })}
          />
        )}

        {(status === 'prepaid' || status === 'pay-per-kit') &&
          canPrepayForSubscription(subscription) && (
            <GenInternalLink
              href={prepayAndSaveLink}
              cta={t.prepaySaveCta}
              testId="prepay-save-link"
              onClick={() =>
                sendItemClickedEvent(
                  prepayAndSaveLink,
                  CTA_NAMES.PREPAY_AND_SAVE_NOW,
                )
              }
              classes={classNames({
                'text-base': isCancelSaverProgramEnabled,
              })}
            />
          )}
      </div>
    </div>
  );
};

const SubscriptionActions: FC<SubscriptionActionsProps> = ({
  t,
  locale,
  user,
  subscription,
  nextUnprocessedItem,
  isLanguageSettingsActionEnabled,
  productDecoratedItem,
  isCancelSaverProgramEnabled,
}) => {
  const { 'sub-squad-enable-cancel-saver-program': cancelSaverEnabled } =
    useFlags();

  const {
    cxCancelSubscriptionLink,
    customerCancelSubscriptionLink,
    reactivateSubscriptionLink,
    cxSkipKitLink,
    customerSkipKitLink,
    removeBookBundleAddonLink,
    editLanguageSettingsLink,
    prepayAndSaveLink,
    continueGiftLink,
    modifyLink,
  } = _getActionLinks(
    t,
    locale,
    user,
    isCancelSaverProgramEnabled,
    subscription,
    nextUnprocessedItem,
  );

  const targetSkipActionLink = isItemOnlySkippableWithCXIntervention(
    nextUnprocessedItem,
  )
    ? cxSkipKitLink
    : customerSkipKitLink;

  const targetCancelActionLink =
    isSubscriptionOnlyCancellableWithCXIntervention(subscription)
      ? cxCancelSubscriptionLink
      : customerCancelSubscriptionLink;

  const subscriptionEventPayload = useMemo(() => {
    return generateSubscriptionEventPayload(
      productDecoratedItem,
      subscription,
      nextUnprocessedItem,
    );
  }, [productDecoratedItem, subscription, nextUnprocessedItem]);

  const handleSkipKitClick = () => {
    sendItemClickedEvent(targetSkipActionLink as string, CTA_NAMES.SKIP_KIT);

    trackSkipKitFlowStartedEvent(subscriptionEventPayload);
  };

  const handleCancelKitClick = () => {
    sendItemClickedEvent(targetCancelActionLink, CTA_NAMES.CANCEL_SUBSCRIPTION);

    trackCancelSubscriptionEvent(subscriptionEventPayload);
  };

  const handleModifySkipClick = () => {
    trackItemClickedEvent({
      url: window.location.href,
      type: CLICKED_ITEM_TYPES.BUTTON,
      pageLocation: PAGE_LOCATIONS.MANAGE_SUBSCRIPTION_TILE,
      cta: t.modifySkipCta,
    });

    // Navigate to the ship date modification page
    window.location.href = modifyLink;
  };

  return (
    <div>
      <div
        className={classNames('ml-[124px] mt-6', {
          'flex flex-row flex-wrap gap-6': !isCancelSaverProgramEnabled,
          'grid gap-y-4 md:gap-y-2 justify-start': isCancelSaverProgramEnabled,
        })}
      >
        {isSubscriptionReactivationAllowed(subscription) && (
          <GenExternalLink
            href={reactivateSubscriptionLink}
            cta={t.reactivateSubscriptionCta}
            onClick={() =>
              sendItemClickedEvent(
                reactivateSubscriptionLink,
                CTA_NAMES.REACTIVATE_SUBSCRIPTION,
              )
            }
          />
        )}

        {cancelSaverEnabled && isSubscriptionActive(subscription) && (
          <Button
            ctaText={t.modifySkipCta}
            onClick={handleModifySkipClick}
            size={BUTTON_SIZES.sm}
            classes="justify-self-start"
          />
        )}

        {!cancelSaverEnabled &&
          isItemSkippable(nextUnprocessedItem) &&
          targetSkipActionLink && (
            <GenExternalLink
              href={targetSkipActionLink}
              cta={t.skipKitCta}
              onClick={handleSkipKitClick}
            />
          )}

        {isSubscriptionCancellationAllowed(subscription) && (
          <GenExternalLink
            href={targetCancelActionLink}
            cta={t.cancelSubscriptionCta}
            onClick={handleCancelKitClick}
            classes={classNames({
              'text-base': isCancelSaverProgramEnabled,
            })}
          />
        )}

        {isLanguageSettingsActionEnabled &&
          isSubscriptionActive(subscription) && (
            <GenExternalLink
              href={editLanguageSettingsLink}
              cta={t.editLanguageSettingsCta}
              classes={classNames({
                'text-base': isCancelSaverProgramEnabled,
              })}
            />
          )}
      </div>

      {!isCancelSaverProgramEnabled && (
        <SubscriptionBookBundleAddOnActions
          t={t}
          subscription={subscription}
          removeBookBundleAddonLink={removeBookBundleAddonLink}
        />
      )}

      <EditPaymentScheduleLink
        locale={locale}
        subscription={subscription}
        t={t}
        user={user}
        nextUnprocessedItem={nextUnprocessedItem}
        prepayAndSaveLink={prepayAndSaveLink}
        continueGiftLink={continueGiftLink}
        isCancelSaverProgramEnabled={isCancelSaverProgramEnabled}
      />
    </div>
  );
};

export default React.memo(SubscriptionActions);
